

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Modal,
  TextField
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack,
  AutoFixHigh,
  Add as AddIcon,
  CloudUpload,
  DesktopWindows,
  TabletMac,
  DashboardCustomizeOutlined, 
  BuildCircleOutlined,
  AutoFixHighOutlined,
  PhoneAndroid,
  Close as CloseIcon,
  SettingsOutlined
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import AutomationsList from './AutomationsList';
// import AutomationWizard from './AutomationWizard';
import AutomationWizard from '../../components/AutomationWizard';
import AutomationLogs from './AutomationLogs';
import FlowBuilder from './FlowBuilder';
import AutomationAnalytics from './AutomationAnalytics';
import transformService from '../../components/AutomationWizard/services/transformService';
import DescribeAutomationDialog from 'components/AiAssistant/DescribeAutomationDialog';
import AutomationCanvas from 'layouts/crm/automation-canvas/AutomationCanvas';
import AutomationsListV2 from 'layouts/crm/automation-canvas/AutomationsListV2';
import useAiAssistantStatus from 'hooks/useAiAssistantStatus';
import PageHeader from "components/v2/PageHeader";

// Desktop-only guard component
const MobileTabletGuard = ({ children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // 1024px and above
  
  if (!isDesktop) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            maxWidth: 500,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <DesktopWindows 
              sx={{ 
                fontSize: 64, 
                color: 'info.main',
                mb: 2 
              }} 
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
              <TabletMac sx={{ fontSize: 32, color: 'text.disabled' }} />
              <PhoneAndroid sx={{ fontSize: 32, color: 'text.disabled' }} />
            </Box>
          </Box>
          
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Desktop Only
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The Automation Builder is available only on desktop computers. 
            Please access this feature from a desktop or laptop with a screen width of at least 1024px.
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            This ensures optimal user experience for drag-and-drop workflow creation 
            and complex automation configuration.
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  return children;
};

function AutomationBuilder() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { user, axiosInstance } = useAuth();
  const [activeView, setActiveView] = useState('list');
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [automationToDelete, setAutomationToDelete] = useState(null);
  const [showCreateChoice, setShowCreateChoice] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);
  const [templateOriginalName, setTemplateOriginalName] = useState('');
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [parsedImports, setParsedImports] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  // AI Assistant: "Describe your automation" drafts into the wizard; nothing is saved until Save.
  const { canUseAiAssistant } = useAiAssistantStatus();
  const [showDescribeDialog, setShowDescribeDialog] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'system_admin' || user?.role === 'SYSTEM_ADMIN';

  // API Functions
  const fetchAutomations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/automations/');
      // console.log('AutomationBuilder: Received automations from backend:', response.data);
      // Show only non-templates in the main list
      const nonTemplates = Array.isArray(response.data)
        ? response.data.filter((a) => !a.is_template)
        : [];
      setAutomations(nonTemplates);
    } catch (err) {
      // console.error('Error fetching automations:', err);
      setError('Failed to load automations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createAutomation = async (automationData) => {
    try {
      const response = await axiosInstance.post('/automations/', automationData);
      setAutomations(prev => [...prev, response.data]);
      setSnackbar({ open: true, message: 'Automation created successfully!', severity: 'success' });
      return response.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        'Failed to create automation. Please try again.';
      setSnackbar({ open: true, message, severity: 'error' });
      throw err;
    }
  };

  const updateAutomation = async (automationId, automationData) => {
    try {
      const response = await axiosInstance.put(`/automations/${automationId}`, automationData);
      setAutomations(prev => prev.map(automation => 
        automation.id === automationId ? response.data : automation
      ));
      setSnackbar({ open: true, message: 'Automation updated successfully!', severity: 'success' });
      return response.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        'Failed to update automation. Please try again.';
      setSnackbar({ open: true, message, severity: 'error' });
      throw err;
    }
  };

  const deleteAutomation = async (automationId) => {
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/automations/${automationId}`);
      setAutomations(prev => prev.filter(automation => automation.id !== automationId));
      setSnackbar({ open: true, message: 'Automation deleted successfully!', severity: 'success' });
    } catch (err) {
      // console.error('Error deleting automation:', err);
      setSnackbar({ open: true, message: 'Failed to delete automation. Please try again.', severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleAutomationStatus = async (automationId) => {
    try {
      const automation = automations.find(a => a.id === automationId);
      const newStatus = automation.is_active ? false : true;
      
      const response = await axiosInstance.patch(`/automations/${automationId}/toggle-status`, {
        is_active: newStatus
      });
      
      setAutomations(prev => prev.map(automation => 
        automation.id === automationId ? { ...automation, is_active: newStatus } : automation
      ));
      
      setSnackbar({ 
        open: true, 
        message: `Automation ${newStatus ? 'activated' : 'deactivated'} successfully!`, 
        severity: 'success' 
      });
    } catch (err) {
      // console.error('Error toggling automation status:', err);
      setSnackbar({ open: true, message: 'Failed to update automation status. Please try again.', severity: 'error' });
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/settings');
    } else {
      fetchAutomations();
    }
  }, [isAdmin, navigate]);

  const handleCreateNew = () => {
    setEditingAutomation(null);
    setShowCreateChoice(true);
  };

  const openCustomAutomation = () => {
    setShowCreateChoice(false);
    setCreatingFromTemplate(false);
    setTemplateOriginalName('');
    setShowWizard(true);
  };

  const normalizeAutomationInput = (item) => {
    try {
      return transformService.serializeAutomation(
        transformService.deserializeAutomation(item)
      );
    } catch (err) {
      return item;
    }
  };

  const parseImports = (text) => {
    setImportText(text);
    try {
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed) ? parsed : [parsed];
      setParsedImports(list);
      setImportError('');
    } catch (err) {
      setParsedImports([]);
      setImportError('Invalid JSON. Please check the structure.');
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    const content = await file.text();
    parseImports(content);
  };

  const handleLoadImportIntoWizard = () => {
    if (!parsedImports.length) {
      setImportError('Nothing to load. Paste or upload a JSON first.');
      return;
    }
    const normalized = normalizeAutomationInput(parsedImports[0]);
    setEditingAutomation(normalized);
    setShowImportModal(false);
    setShowWizard(true);
  };

  const handleBatchImport = async () => {
    if (!parsedImports.length) {
      setImportError('Nothing to import. Paste or upload a JSON first.');
      return;
    }
    setSaving(true);
    try {
      for (const item of parsedImports) {
        const normalized = normalizeAutomationInput(item);
        await createAutomation(normalized);
      }
      setSnackbar({
        open: true,
        message: `Imported ${parsedImports.length} automations`,
        severity: 'success'
      });
      setShowImportModal(false);
      setImportText('');
      setParsedImports([]);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error importing automations: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const res = await axiosInstance.get('/automations/templates');
      setTemplates(res.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load templates', severity: 'error' });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openTemplates = async () => {
    setShowCreateChoice(false);
    await fetchTemplates();
    setShowTemplatesModal(true);
  };

  const handleUseTemplate = (tpl) => {
    setShowTemplatesModal(false);
    const initialFromTemplate = tpl ? {
      ...tpl,
      id: null,
      name: '',
      is_template: false
    } : null;
    setCreatingFromTemplate(true);
    setTemplateOriginalName(tpl?.name || '');
    setEditingAutomation(initialFromTemplate);
    setShowWizard(true);
  };

  // A flow with branches or business-day waits, or a scheduled trigger, is
  // more than the classic wizard and flow view can show; editing it there
  // would flatten it. Classic leaves it to the new look.
  const builtInFlowBuilder = (automation) =>
    !isV2 && (Boolean(automation?.flow) || automation?.trigger_event === 'on schedule');
  const flowBuilderNotice = () =>
    setSnackbar({
      open: true,
      message: 'This automation was built in the flow builder (branches, business-day waits or a schedule). Switch to the new look to change it.',
      severity: 'info',
    });

  const handleEditAutomation = (automation) => {
    // console.log('AutomationBuilder: handleEditAutomation called with:', automation);
    if (builtInFlowBuilder(automation)) {
      flowBuilderNotice();
      return;
    }
    setEditingAutomation(automation);
    setShowWizard(true);
  };

  const handleViewLogs = (automation) => {
    setSelectedAutomation(automation);
    setActiveView('logs');
  };

  const handleViewFlowBuilder = (automation) => {
    if (builtInFlowBuilder(automation)) {
      flowBuilderNotice();
      return;
    }
    setSelectedAutomation(automation);
    setActiveView('flow');
  };

  const handleViewAnalytics = async () => {
    setActiveView('analytics');
    await fetchAnalyticsData();
  };

  const handleWizardClose = () => {
    setShowWizard(false);
    setEditingAutomation(null);
  };

  const handleWizardSave = async (automationData) => {
    try {
      const proposedName = (automationData?.name || '').trim();
      const excludeId =
        editingAutomation?.id ||
        (activeView === 'flow' ? selectedAutomation?.id : null) ||
        null;
      const duplicate = (automations || []).find(
        (a) =>
          a.id !== excludeId &&
          (a.name || '').trim().toLowerCase() === proposedName.toLowerCase()
      );
      if (duplicate) {
        const message = `An automation named "${proposedName}" already exists. Choose a different name.`;
        setSnackbar({ open: true, message, severity: 'error' });
        throw new Error(message);
      }

      // If creating from a template, enforce name change and set flags
      if (creatingFromTemplate) {
        const originalName = templateOriginalName || '';
        const newName = proposedName;
        if (!newName || newName === originalName) {
          setSnackbar({ open: true, message: 'Please change the Automation Name before saving.', severity: 'error' });
          throw new Error('Name must be changed when creating from a template');
        }
        automationData = {
          ...automationData,
          is_template: false,
          template_status: 'inactive'
        };
        // Always create a new automation when coming from a template
        await createAutomation(automationData);
      } else if (editingAutomation?.id) {
        await updateAutomation(editingAutomation.id, automationData);
      } else if (selectedAutomation?.id && activeView === 'flow') {
        // Flow Builder sets selectedAutomation, not editingAutomation — must PUT
        // the existing row. Creating instead left a duplicate whose Created Date
        // matched Modified Date and looked like an overwrite on edit.
        const updated = await updateAutomation(selectedAutomation.id, automationData);
        setSelectedAutomation(updated);
      } else {
        await createAutomation(automationData);
      }
      setShowWizard(false);
      setEditingAutomation(null);
      setCreatingFromTemplate(false);
      setTemplateOriginalName('');
    } catch (err) {
      // Error handling is done in the API functions
      throw err;
    }
  };

  const handleToggleStatus = (automationId) => {
    toggleAutomationStatus(automationId);
  };

  const handleDeleteAutomation = (automationId) => {
    deleteAutomation(automationId);
  };

  // Open delete modal from list
  const handleRequestDelete = (automation) => {
    setAutomationToDelete(automation);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAutomationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!automationToDelete) return;
    await deleteAutomation(automationToDelete.id);
    handleCloseDeleteModal();
  };

  const handleBackToList = () => {
    setActiveView('list');
    setSelectedAutomation(null);
  };

  const fetchAnalyticsData = async () => {
    try {
      setAnalyticsLoading(true);
      const [metricsResponse, queueResponse] = await Promise.all([
        axiosInstance.get('/automations/analytics/overall-metrics'),
        axiosInstance.get('/automations/analytics/queue/status')
      ]);
      setAnalyticsData({
        metrics: metricsResponse.data,
        queue: queueResponse.data
      });
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setSnackbar({ open: true, message: 'Failed to load analytics data', severity: 'error' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const renderHeader = () => {
    const getTitle = () => {
      switch (activeView) {
        case 'logs':
          return `Automation Logs - ${selectedAutomation?.name}`;
        case 'flow':
          return `Flow Builder - ${selectedAutomation?.name}`;
        case 'analytics':
          return 'Automation Analytics & Queue Status';
        default:
          return 'Automation Builder';
      }
    };

    const subtitle =
      activeView === 'list' ? 'Create and manage automated workflows for your CRM' :
      activeView === 'logs' ? 'View execution history and performance metrics' :
      activeView === 'analytics' ? 'Monitor automation performance and queue health' :
      'Visual workflow designer with drag-and-drop interface';

    // v2: the canvas builder draws its own header.
    if (isV2 && (showWizard || activeView === 'flow')) return null;

    // v2: the standard page header. Sub-views keep their way back to the
    // list as a header button; the breadcrumb already leads to Settings.
    if (isV2) {
      return (
        <PageHeader
          title={activeView === 'list' ? 'Automations' : getTitle()}
          summary={activeView === 'list'
            ? `${automations.filter((a) => a.is_active).length} active · ${automations.filter((a) => !a.is_active).length} paused`
            : subtitle}
          actions={activeView === 'list' ? (
            <>
              {canUseAiAssistant && (
                <MDButton variant="outlined" color="dark" onClick={() => setShowDescribeDialog(true)}>
                  Describe with Seep
                </MDButton>
              )}
              <MDButton variant="outlined" color="dark" startIcon={<CloudUpload />} onClick={() => setShowImportModal(true)}>
                Import JSON
              </MDButton>
              <MDButton variant="gradient" color="info" startIcon={<AddIcon />} onClick={handleCreateNew}>
                Create Automation
              </MDButton>
            </>
          ) : (
            <MDButton variant="outlined" color="dark" startIcon={<ArrowBack />} onClick={handleBackToList}>
              Back to automations
            </MDButton>
          )}
        />
      );
    }

    return (
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
          <IconButton
            onClick={activeView === 'list' ? () => navigate('/settings') : handleBackToList}
            aria-label={activeView === 'list' ? 'Back to settings' : 'Back to automations'}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'surface.container', color: 'text.primary' } }}
          >
            <ArrowBack />
          </IconButton>
          <Box
            sx={(theme) => ({
              width: 48,
              height: 48,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1)
            })}
          >
            <AutoFixHigh sx={{ fontSize: 26 }} />
          </Box>
          <MDBox minWidth={0}>
            <Typography
              component="h1"
              sx={{ fontSize: '24px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
            >
              {getTitle()}
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          </MDBox>
        </MDBox>
        {activeView === 'list' && (
          <Box display="flex" gap={1.5}>
            <MDButton
              variant="outlined"
              color="info"
              startIcon={<CloudUpload />}
              onClick={() => setShowImportModal(true)}
            >
              Import JSON
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Create Automation
            </MDButton>
          </Box>
        )}
      </MDBox>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </MDBox>
      );
    }

    if (error) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {error}
            <MDButton 
              variant="text" 
              onClick={fetchAutomations}
              sx={{ ml: 2 }}
            >
              Retry
            </MDButton>
          </Alert>
        </MDBox>
      );
    }

    // v2: creating and editing happen on the canvas (layouts/crm/
    // automation-canvas) instead of the wizard dialog; same state and save.
    if (isV2 && showWizard) {
      return (
        <AutomationCanvas
          automation={editingAutomation}
          onClose={handleWizardClose}
          onSave={handleWizardSave}
        />
      );
    }

    switch (activeView) {
      case 'logs':
        return (
          <AutomationLogs 
            automation={selectedAutomation}
            onBack={handleBackToList}
          />
        );
      case 'flow':
        if (isV2) {
          return (
            <AutomationCanvas
              automation={selectedAutomation}
              onClose={handleBackToList}
              onSave={handleWizardSave}
            />
          );
        }
        return (
          <FlowBuilder 
            automation={selectedAutomation}
            onBack={handleBackToList}
            onSave={handleWizardSave}
          />
        );
      case 'analytics':
        return (
          <AutomationAnalytics 
            data={analyticsData}
            loading={analyticsLoading}
            onRefresh={fetchAnalyticsData}
          />
        );
      default:
        if (isV2) {
          return (
            <AutomationsListV2
              automations={automations}
              onOpen={handleEditAutomation}
              onViewLogs={handleViewLogs}
              onToggleStatus={handleToggleStatus}
              onRequestDelete={handleRequestDelete}
            />
          );
        }
        return (
          <AutomationsList
            automations={automations}
            onEdit={handleEditAutomation}
            onViewLogs={handleViewLogs}
            onViewFlow={handleViewFlowBuilder}
            onToggleStatus={handleToggleStatus}
            onDelete={handleDeleteAutomation}
            loading={loading}
            isDeleting={isDeleting}
            onRequestDelete={handleRequestDelete}
            onViewAnalytics={handleViewAnalytics}
          />
        );
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MobileTabletGuard>
        <MDBox pt={isV2 ? 3 : 6} pb={3}>
          {renderHeader()}
          {renderContent()}
        </MDBox>

        {/* Delete Confirmation Modal (styled similar to LeadDetail) */
        }
        <Modal
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          aria-labelledby="automation-delete-modal-title"
          aria-describedby="automation-delete-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <Box
              sx={{
                bgcolor: 'surface.main',
                borderBottom: '1px solid',
                borderColor: 'outline.variant',
                px: 4,
                py: 2.5,
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                id="automation-delete-modal-title"
                component="h2"
                sx={{ fontSize: '18px', fontWeight: 600, color: 'text.primary' }}
              >
                Delete Automation
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 4 }}>
              <Typography
                id="automation-delete-modal-description"
                sx={{ mb: 3, fontSize: '15px', color: 'text.primary' }}
              >
                Are you sure you want to delete this automation? This action cannot be undone.
              </Typography>

              <MDBox display="flex" justifyContent="flex-end" mt={3}>
                <MDButton
                  onClick={handleCloseDeleteModal}
                  variant="outlined"
                  color="info"
                  sx={{ marginRight: 2 }}
                >
                  Cancel
                </MDButton>
                <MDButton
                  onClick={handleConfirmDelete}
                  variant="gradient"
                  color="error"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </MDButton>
              </MDBox>
            </Box>
          </Box>
        </Modal>

        {/* Wizard Modal */}
        {showWizard && !isV2 && (

          <AutomationWizard
            open={showWizard}
            automation={editingAutomation}
            onClose={handleWizardClose}
            onSave={handleWizardSave}
          />
        )}

        {/* Create choice modal */}
        <Modal
          open={showCreateChoice}
          onClose={() => setShowCreateChoice(false)}
          aria-labelledby="automation-create-choice-title"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'outline.variant'
          }}>
            {/* Header with gradient background */}
            <Box sx={{
              p: 3,
              pb: 2,
              bgcolor: 'surface.main',
              borderBottom: '1px solid',
              borderColor: 'outline.variant'
            }}>
              {/* Close button */}
              <IconButton
                onClick={() => setShowCreateChoice(false)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'surface.container',
                    color: 'primary.main'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AutoFixHighOutlined sx={{ color: 'primary.main', mr: 1.5, fontSize: 24 }} />
                <Typography 
                  id="automation-create-choice-title" 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Create Automation
                </Typography>
              </Box>
            </Box>
            
            {/* Content Area */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', minHeight: '320px' }}>
              {/* Template Section */}
              <Box
                sx={{ 
                  flex: 1,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'surface.main',
                    '& .template-stack': {
                      '& > div': {
                        transform: 'translateX(0) rotate(0deg)',
                        opacity: 1
                      }
                    }
                  }
                }}
              >
                {/* Animated Template Stack */}
                <Box 
                  className="template-stack"
                  sx={{ 
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    width: 50,
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        position: 'absolute',
                        width: 30 - (i * 3),
                        height: 35 - (i * 3),
                        borderRadius: '4px',
                        bgcolor: 'primary.main',
                        opacity: 0.2 + (0.2 * (2 - i)),
                        transform: `translateX(${i * 8}px) rotate(${i * 5}deg)`,
                        transition: 'all 0.5s ease',
                        border: '1px solid',
                        borderColor: 'background.paper',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '30%',
                          left: '20%',
                          right: '20%',
                          height: '2px',
                          bgcolor: 'background.paper',
                          borderRadius: '1px'
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: '20%',
                          right: '35%',
                          height: '2px',
                          bgcolor: 'background.paper',
                          borderRadius: '1px'
                        }
                      }}
                    />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'surface.container',
                    mr: 2,
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <DashboardCustomizeOutlined 
                      sx={{ 
                        color: 'primary.main', 
                        fontSize: 22, 
                        zIndex: 1,
                        animation: 'slideUpDown 3s ease-in-out infinite',
                        '@keyframes slideUpDown': {
                          '0%, 100%': { transform: 'translateY(0)' },
                          '25%': { transform: 'translateY(-3px)' },
                          '75%': { transform: 'translateY(3px)' }
                        }
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '16px',
                      color: 'text.primary'
                    }}
                  >
                    Use Template
                  </Typography>
                </Box>

                {/* Template preview animation with proper overflow */}
                <Box sx={{ 
                  mb: 2.5, 
                  height: 35, 
                  position: 'relative',
                  overflow: 'hidden',
                  mx: -1,
                  px: 1
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.5,
                    position: 'absolute',
                    animation: 'slideShow 8s linear infinite',
                    '@keyframes slideShow': {
                      '0%': { transform: 'translateX(0)' },
                      '100%': { transform: 'translateX(-50%)' }
                    }
                  }}>
                    {/* Duplicate the array for seamless loop */}
                    {[...Array(2)].map((_, groupIndex) => (
                      ['Email Automation', 'Lead Scoring', 'Task Assignment', 'Data Sync'].map((label, i) => (
                        <Box
                          key={`${groupIndex}-${i}`}
                          sx={{
                            minWidth: 100,
                            height: 30,
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: 'outline.variant',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1.5,
                            bgcolor: 'background.paper',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Typography sx={{ 
                            fontSize: '13px', 
                            fontWeight: 600,
                            color: 'primary.main',
                            whiteSpace: 'nowrap'
                          }}>
                            {label}
                          </Typography>
                        </Box>
                      ))
                    ))}
                  </Box>
                </Box>

                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    mb: 3,
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Start quickly with pre-built automation templates tailored for common use cases.
                </Typography>
                
                <Box sx={{ mt: 'auto' }}>
                  <MDButton 
                    variant="gradient" 
                    color="info" 
                    fullWidth 
                    onClick={openTemplates}
                    sx={{ height: '45px', borderRadius: '8px' }}
                  >
                    Browse Templates
                  </MDButton>
                </Box>
              </Box>
              
              {/* Vertical Divider */}
              <Box sx={{ width: '1px', bgcolor: 'outline.variant', my: 2, display: { xs: 'none', md: 'block' } }} />

              {/* Custom Section */}
              <Box
                sx={{ 
                  flex: 1,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'surface.main',
                    '& .gear-animation': {
                      animation: 'rotate 4s linear infinite'
                    }
                  }
                }}
              >
                {/* Animated Gear */}
                <Box
                  className="gear-animation"
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    '@keyframes rotate': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <SettingsOutlined sx={{ color: 'primary.main', opacity: 0.2, fontSize: 30 }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'surface.container',
                    mr: 2,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '10px',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      opacity: 0,
                      animation: 'ping 2s infinite',
                      '@keyframes ping': {
                        '0%': {
                          opacity: 0.8,
                          transform: 'scale(1)'
                        },
                        '100%': {
                          opacity: 0,
                          transform: 'scale(1.3)'
                        }
                      }
                    }
                  }}>
                    <BuildCircleOutlined sx={{ color: 'primary.main', fontSize: 22, zIndex: 1 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '16px',
                      color: 'text.primary'
                    }}
                  >
                    Build Custom
                  </Typography>
                </Box>

                {/* Building blocks animation */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 30 }}>
                  {[20, 30, 15, 25].map((height, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 20,
                        height: `${height}px`,
                        borderRadius: 1,
                        bgcolor: 'primary.main',
                        opacity: 0.3 + (i * 0.15),
                        animation: `build ${1.5}s ease-in-out infinite`,
                        animationDelay: `${i * 0.15}s`,
                        '@keyframes build': {
                          '0%, 100%': {
                            transform: 'scaleY(1)',
                            opacity: 0.3
                          },
                          '50%': {
                            transform: 'scaleY(1.2)',
                            opacity: 0.8
                          }
                        }
                      }}
                    />
                  ))}
                </Box>

                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    mb: 3,
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Create a custom automation tailored to your specific workflow requirements.
                </Typography>
                
                <Box sx={{ mt: 'auto' }}>
                  <MDButton 
                    variant="gradient" 
                    color="info" 
                    fullWidth 
                    onClick={openCustomAutomation}
                    sx={{ height: '45px', borderRadius: '8px' }}
                  >
                    Start Building
                  </MDButton>
                </Box>
              </Box>

            </Box>
            {canUseAiAssistant && (
              <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'outline.variant', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                  Or describe it in your own words and review the draft in the wizard.
                </Typography>
                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<AutoFixHigh />}
                  onClick={() => { setShowCreateChoice(false); setShowDescribeDialog(true); }}
                  sx={{ flexShrink: 0 }}
                >
                  Describe your automation
                </MDButton>
              </Box>
            )}
            </Box>
        </Modal>

        {showDescribeDialog && (
          <DescribeAutomationDialog
            open
            onClose={() => setShowDescribeDialog(false)}
            onDraft={(draft) => {
              setShowDescribeDialog(false);
              setCreatingFromTemplate(false);
              setTemplateOriginalName('');
              setEditingAutomation(normalizeAutomationInput({ ...draft, id: null }));
              setShowWizard(true);
            }}
          />
        )}

        {/* Import modal */}
        <Modal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          aria-labelledby="automation-import-title"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'outline.variant'
          }}>
            <Box sx={{
              p: 3,
              pb: 2,
              bgcolor: 'surface.main',
              borderBottom: '1px solid',
              borderColor: 'outline.variant',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Import Automations
              </Typography>
              <IconButton
                onClick={() => setShowImportModal(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'surface.container',
                    color: 'primary.main'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Upload or paste automation JSON. Load one into the wizard for editing, or batch-create all.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MDButton
                  variant="outlined"
                  color="info"
                  component="label"
                  size="small"
                >
                  Upload file
                  <input
                    type="file"
                    accept=".json,application/json"
                    hidden
                    onChange={(e) => handleImportFile(e.target.files?.[0])}
                  />
                </MDButton>
                <Typography variant="caption" color="text.secondary">
                  or paste JSON below
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={6}
                placeholder='[{ "name": "My Automation", "trigger_object": "lead", "trigger_event": "on create", "actions": [] }]'
                value={importText}
                onChange={(e) => parseImports(e.target.value)}
                error={!!importError}
                helperText={importError || `${parsedImports.length} automation${parsedImports.length === 1 ? '' : 's'} parsed`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px'
                  }
                }}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={!parsedImports.length}
                  onClick={handleLoadImportIntoWizard}
                  sx={{ height: 44 }}
                >
                  Load first into wizard
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="info"
                  fullWidth
                  disabled={!parsedImports.length || saving}
                  onClick={handleBatchImport}
                  sx={{ height: 44 }}
                >
                  {saving ? 'Importing...' : `Batch create (${parsedImports.length || 0})`}
                </MDButton>
              </Box>
            </Box>
          </Box>
        </Modal>
        {/* Templates table modal */}
        <Modal
          open={showTemplatesModal}
          onClose={() => setShowTemplatesModal(false)}
          aria-labelledby="automation-templates-title"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(1000px, calc(100vw - 48px))',
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <Box
              sx={{
                bgcolor: 'surface.main',
                borderBottom: '1px solid',
                borderColor: 'outline.variant',
                px: 4,
                py: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography
                  id="automation-templates-title"
                  component="h2"
                  sx={{ fontSize: '18px', fontWeight: 600, color: 'text.primary' }}
                >
                  Prebuilt Automation Templates
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  Pick a template to preload the wizard, then give it a name
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShowTemplatesModal(false)}
                aria-label="Close"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'surface.container', color: 'text.primary' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 3, minHeight: 0, overflowY: 'auto', bgcolor: 'surface.main' }}>
              {loadingTemplates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : templates.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.primary">No templates available</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try refreshing the catalog or build a custom automation.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {templates.map((tpl) => {
                    const object = tpl.trigger_object || tpl.object || '';
                    const actionLabels = Array.isArray(tpl.actions)
                      ? tpl.actions.map((a) => a?.type || a?.action || a?.name).filter(Boolean)
                      : [];

                    return (
                      <Grid item xs={12} md={6} key={tpl.id}>
                        <Card
                          sx={{
                            height: '100%',
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'outline.variant',
                            boxShadow: 'none',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 3
                            }
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                              {tpl.name}
                            </Typography>
                            {tpl.category && (
                              <Typography variant="caption" color="primary">
                                {tpl.category}
                              </Typography>
                            )}
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            {tpl.description || ''}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 'auto' }}>
                            {object && (
                              <Chip
                                size="small"
                                label={object.charAt(0).toUpperCase() + object.slice(1)}
                                sx={{ bgcolor: 'surface.container', color: 'text.primary' }}
                              />
                            )}
                            {actionLabels.map((label, i) => (
                              <Chip
                                key={`${tpl.id}-action-${i}`}
                                size="small"
                                variant="outlined"
                                label={label}
                                sx={{ borderColor: 'outline.variant', color: 'text.secondary' }}
                              />
                            ))}
                          </Box>

                          <Box>
                            <MDButton
                              size="small"
                              variant="gradient"
                              color="info"
                              fullWidth
                              onClick={() => handleUseTemplate(tpl)}
                            >
                              Use this template
                            </MDButton>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>
        </Modal>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MobileTabletGuard>
      
    </DashboardLayout>
  );
}

export default AutomationBuilder; 
